import * as i0 from '@angular/core';
import { EventEmitter, forwardRef, Component, ChangeDetectionStrategy, ViewEncapsulation, Input, HostBinding, ViewChild, Output, NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NG_VALUE_ACCESSOR, FormsModule } from '@angular/forms';
import * as i1 from '@ng-bootstrap/ng-bootstrap';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { Subject, NEVER, merge } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map } from 'rxjs/operators';

class TypeaheadComponent {
    constructor(changeDetectorRef) {
        this.changeDetectorRef = changeDetectorRef;
        this.options = [];
        this.disabled = false;
        this.editable = true;
        this.limit = 20;
        this.debounceTime = 200;
        this.focus$ = new Subject();
        this.click$ = new Subject();
        this.selectChange = new EventEmitter();
        this.search = (text$) => {
            const debouncedText$ = text$.pipe(debounceTime(this.debounceTime), distinctUntilChanged());
            const clicksWithClosedPopup$ = this.click$.pipe(filter(() => !this.instance.isPopupOpen()));
            const inputFocus$ = this.focus$;
            if (this.options.length == 0) {
                return NEVER;
            }
            return merge(debouncedText$, inputFocus$, clicksWithClosedPopup$).pipe(map((term) => {
                if (!term) {
                    return this.options;
                }
                return this.options
                    .filter(v => v.toLowerCase().indexOf(term.toLowerCase()) > -1)
                    .slice(0, this.limit);
            }));
        };
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    writeValue(value) {
        this.value = value || '';
        this.changeDetectorRef.detectChanges();
    }
    setDisabledState(disabled) {
        this.disabled = disabled;
    }
    focus(target) {
        target.select();
        this.focus$.next('');
    }
    click(target) {
        target.select();
        this.click$.next('');
    }
    update(value) {
        if (!this.editable)
            return;
        this.select(value);
    }
    select(value) {
        this.value = value;
        this.onChange(value);
        this.onTouched();
        // Workaround for change event not firing on enclosing form element.
        this.selectChange.next(value);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: TypeaheadComponent, deps: [{ token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.1.3", type: TypeaheadComponent, selector: "rcp-typeahead", inputs: { options: "options", disabled: "disabled", editable: "editable", limit: "limit", debounceTime: "debounceTime" }, outputs: { selectChange: "selectChange" }, host: { properties: { "class.disabled": "this.disabled" } }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => TypeaheadComponent),
                multi: true
            }
        ], viewQueries: [{ propertyName: "instance", first: true, predicate: ["instance"], descendants: true, static: true }], ngImport: i0, template: "<input type=\"text\"\n  popupClass=\"papers-scrollbar\"\n  #instance=\"ngbTypeahead\"\n\t[ngbTypeahead]=\"search\"\n  [editable]=\"editable\"\n  [disabled]=\"disabled\"\n  [value]=\"value\"\n  (keyup)=\"update($event.target.value)\"\n  (selectItem)=\"select($event.item)\"\n\t(focus)=\"focus($any($event).target)\"\n\t(click)=\"click($any($event).target)\">\n", styles: ["input{width:100%;padding:0;border:0;background:transparent;color:inherit}\n"], dependencies: [{ kind: "directive", type: i1.NgbTypeahead, selector: "input[ngbTypeahead]", inputs: ["autocomplete", "container", "editable", "focusFirst", "inputFormatter", "ngbTypeahead", "resultFormatter", "resultTemplate", "selectOnExact", "showHint", "placement", "popperOptions", "popupClass"], outputs: ["selectItem"], exportAs: ["ngbTypeahead"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: TypeaheadComponent, decorators: [{
            type: Component,
            args: [{ selector: 'rcp-typeahead', changeDetection: ChangeDetectionStrategy.OnPush, encapsulation: ViewEncapsulation.None, providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => TypeaheadComponent),
                            multi: true
                        }
                    ], template: "<input type=\"text\"\n  popupClass=\"papers-scrollbar\"\n  #instance=\"ngbTypeahead\"\n\t[ngbTypeahead]=\"search\"\n  [editable]=\"editable\"\n  [disabled]=\"disabled\"\n  [value]=\"value\"\n  (keyup)=\"update($event.target.value)\"\n  (selectItem)=\"select($event.item)\"\n\t(focus)=\"focus($any($event).target)\"\n\t(click)=\"click($any($event).target)\">\n", styles: ["input{width:100%;padding:0;border:0;background:transparent;color:inherit}\n"] }]
        }], ctorParameters: () => [{ type: i0.ChangeDetectorRef }], propDecorators: { options: [{
                type: Input
            }], disabled: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['class.disabled']
            }], editable: [{
                type: Input
            }], limit: [{
                type: Input
            }], debounceTime: [{
                type: Input
            }], instance: [{
                type: ViewChild,
                args: ['instance', { static: true }]
            }], selectChange: [{
                type: Output
            }] } });

class ReadCubeTypeaheadModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: ReadCubeTypeaheadModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.1.3", ngImport: i0, type: ReadCubeTypeaheadModule, declarations: [TypeaheadComponent], imports: [CommonModule,
            FormsModule,
            NgbModule], exports: [TypeaheadComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: ReadCubeTypeaheadModule, imports: [CommonModule,
            FormsModule,
            NgbModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: ReadCubeTypeaheadModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        FormsModule,
                        NgbModule,
                    ],
                    declarations: [
                        TypeaheadComponent,
                    ],
                    exports: [
                        TypeaheadComponent,
                    ],
                    providers: []
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { ReadCubeTypeaheadModule, TypeaheadComponent };
